import { useRef, useEffect, useCallback } from "react";

interface UseHistoryOptions {
    maxHistorySize?: number;
}

export function useHistory<T>(
    currentState: T,
    setState: (state: T) => void,
    options: UseHistoryOptions = {},
) {
    const { maxHistorySize = 30 } = options;
    const undoHistoryRef = useRef<T[]>([]);
    const redoHistoryRef = useRef<T[]>([]);
    const currentStateRef = useRef<T>(currentState);

    // Update current state ref whenever state changes
    useEffect(() => {
        currentStateRef.current = currentState;
    }, [currentState]);

    const addToHistory = useCallback(() => {
        undoHistoryRef.current.push({ ...currentStateRef.current });
        // Clear redo stack when new action is performed
        redoHistoryRef.current = [];
        // Keep only last N states
        if (undoHistoryRef.current.length > maxHistorySize) {
            undoHistoryRef.current.shift();
        }
    }, [maxHistorySize]);

    const undo = useCallback(() => {
        const previousState = undoHistoryRef.current.pop();
        if (previousState) {
            // Save current state to redo stack before undoing
            redoHistoryRef.current.push({ ...currentStateRef.current });
            setState(previousState);
        }
    }, [setState]);

    const redo = useCallback(() => {
        const nextState = redoHistoryRef.current.pop();
        if (nextState) {
            // Save current state to undo stack before redoing
            undoHistoryRef.current.push({ ...currentStateRef.current });
            setState(nextState);
        }
    }, [setState]);

    // Add keyboard event listener for undo/redo
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            // Check if we're in a text input
            const isTextInput =
                e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLInputElement;
            if (isTextInput) return;

            if ((e.metaKey || e.ctrlKey) && e.key === "z") {
                e.preventDefault();
                if (e.shiftKey) {
                    redo();
                } else {
                    undo();
                }
            }
            // Additional shortcut for redo (Ctrl+Y)
            if ((e.metaKey || e.ctrlKey) && e.key === "y") {
                e.preventDefault();
                redo();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [redo, undo]);

    return {
        addToHistory,
        undo,
        redo,
    };
}
